import { register, SwiperContainer } from 'swiper/element';
import { A11y, Autoplay, Navigation } from 'swiper/modules';
import { AutoplayOptions, Swiper, SwiperOptions } from 'swiper/types';
import { getNavigationA11yParams, stripInvalidAriaControls } from './SwiperA11y';

export class SecondarySliderInitializer {
	/**
	 * Initializes the Slider.
	 *
	 * @returns void
	 */
	init() {
		register();

		// Get all swipers and initialize them with the given parameters
		const swiperElements = document.querySelectorAll<SwiperContainer>(
			'.slider-module-container.secondary swiper-container'
		);
		if (!swiperElements.length) return;

		swiperElements.forEach((swiper: SwiperContainer) => {
			const container = swiper.closest('.slider-module-container.secondary');
			const swiperButtonPrev = container?.querySelector<HTMLElement>('.swiper-button-prev');
			const swiperButtonNext = container?.querySelector<HTMLElement>('.swiper-button-next');
			const navigationContainer = container?.querySelector<HTMLElement>('.slider-navigation');

			// Get parameters transmitted by backend settings
			let slidesPerView: number | 'auto' = +swiper.getAttribute('data-slidesPerView')!;
			if (!slidesPerView) {
				slidesPerView = 'auto';
			}
			const loop = swiper.getAttribute('data-loop') === 'true';
			const autoplay = swiper.getAttribute('data-autoplay');
			const autoplayInterval = +(swiper.getAttribute('data-autoplayInterval') || 3000);

			let autoplayParams: AutoplayOptions | boolean = false;
			if (autoplay != 'false') {
				autoplayParams = {
					delay: autoplayInterval,
					disableOnInteraction: true,
					pauseOnMouseEnter: true,
					stopOnLastSlide: true,
				};
			}

			const responsiveSlidesPerView = this.getResponsiveSlides(slidesPerView);

			const swiperParams: SwiperOptions = {
				modules: [Navigation, Autoplay, A11y],
				slidesPerView: responsiveSlidesPerView.mobile,
				spaceBetween: 16,
				loop: loop,
				autoplay: autoplayParams,
				preventInteractionOnTransition: true,
				a11y: getNavigationA11yParams(navigationContainer),
				freeMode: {
					enabled: false,
					sticky: true,
				},
				grabCursor: true,
				observer: true,
				observeParents: true,
				resizeObserver: true,
				navigation: {
					prevEl: swiperButtonPrev,
					nextEl: swiperButtonNext,
				},

				breakpoints: {
					980: {
						slidesPerView: responsiveSlidesPerView.desktop,
						spaceBetween: 44,
					},
					767: {
						slidesPerView: responsiveSlidesPerView.tablet,
						spaceBetween: 22,
					},
				},

				injectStyles: [
					`
					:host .swiper.swiper-initialized {
						overflow: visible;
					}
				`,
				],

				watchOverflow: true,
				on: {
					init: (swiper) => this.toggleNav(swiper),
					resize: (swiper) => this.toggleNav(swiper),
				},
			};

			Object.assign(swiper, swiperParams);
			swiper.initialize();
			stripInvalidAriaControls(swiperButtonPrev, swiperButtonNext);
		});
	}

	getResponsiveSlides(slidesPerView: number | 'auto'): ResponsiveSlidesPerView {
		// TODO: Is that correct for Gallery
		if (slidesPerView === 'auto') {
			return new ResponsiveSlidesPerView('auto', 'auto', 'auto');
		}

		const tablet = slidesPerView >= 3 ? 2.3 : slidesPerView == 2 ? 2 : 1;
		const mobile = slidesPerView >= 2 ? 1.3 : 1;

		return new ResponsiveSlidesPerView(slidesPerView, tablet, mobile);
	}

	toggleNav(swiper: Swiper): void {
		const navElement = swiper.slidesEl.parentElement!.querySelector<HTMLElement>('.slider-navigation');
		if (navElement === null) return;

		const visibleCount =
			swiper.params.slidesPerView === 'auto'
				? swiper.slidesPerViewDynamic()
				: (swiper.params.slidesPerView as number);

		if (swiper.slides.length <= visibleCount) {
			navElement.classList.add('hidden');
		} else {
			navElement.classList.remove('hidden');
		}
	}
}

class ResponsiveSlidesPerView {
	desktop: number | 'auto';
	tablet: number | 'auto';
	mobile: number | 'auto';

	constructor(desktop: number | 'auto', tablet: number | 'auto', mobile: number | 'auto') {
		this.desktop = desktop;
		this.tablet = tablet;
		this.mobile = mobile;
	}
}
